import { clamp, type NormalizedRect } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { BackgroundIssue, BackgroundMetrics } from '../types/metrics.js';
import { rgbAt } from '../image/analysis.js';
import { mean, median, round } from '../geometry/statistics.js';

/**
 * Background analysis.
 *
 * Fully local, model-free heuristics today, with an explicit seam for a real
 * segmentation model later: everything downstream consumes `BackgroundMetrics`
 * (plus the issue rectangles), so swapping in an ONNX segmentation mask only
 * changes this file.
 *
 * What is measured:
 *  - reference background colour taken from the outer ring of the frame;
 *  - per-cell uniformity and local shadows on a coarse grid;
 *  - "foreign" pixels outside the subject box (objects, patterns, other people);
 *  - edge density (busy background);
 *  - colour cast (not neutral) and deviation from the required colour.
 *
 * Every finding carries a normalized rectangle, which is what the overlay uses
 * to point at the exact spot on the user's photo.
 */

export interface BackgroundOptions {
  /** Extra padding around the subject rect that is excluded from "background". */
  subjectPadding: number;
  /**
   * Extra horizontal padding, as a fraction of the subject width, that accounts
   * for hair, ears and headwear. The detector's box covers the *face*; the rest
   * of the head sticks out sideways, and treating it as background used to
   * report the person's own hair as "clutter" and a "shadow".
   */
  hairPadding: number;
  /** Extra padding above the face box, as a fraction of its height (hair). */
  hairTopPadding: number;
  /** Thickness of the outer ring used to sample the reference colour. */
  ringThickness: number;
  /** RGB distance under which a pixel counts as "background coloured". */
  colourTolerance: number;
  /** Luma gradient above which a background pixel counts as an edge. */
  edgeThreshold: number;
  /** Grid resolution for local shadow/clutter detection. */
  gridSize: number;
  /** Luma difference from the ring reference that counts as a local shadow. */
  shadowDelta: number;
  /** Share of foreign pixels in a cell that flags it as clutter. */
  clutterCellRatio: number;
  /** Minimum background pixels required for a meaningful verdict. */
  minBackgroundPixels: number;
}

export const DEFAULT_BACKGROUND_OPTIONS: BackgroundOptions = {
  subjectPadding: 0.04,
  hairPadding: 0.14,
  hairTopPadding: 0.35,
  ringThickness: 0.07,
  colourTolerance: 42,
  edgeThreshold: 24,
  gridSize: 6,
  shadowDelta: 20,
  clutterCellRatio: 0.3,
  minBackgroundPixels: 400,
};

export interface BackgroundAnalysisInput {
  buffer: AnalysisBuffer;
  /** Where the head is; excluded from the background statistics. */
  subjectRect: NormalizedRect;
  /** Colour the document requires, e.g. pure white for the US visa photo. */
  targetColor?: { r: number; g: number; b: number };
  options?: Partial<BackgroundOptions>;
}

interface CellStats {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  count: number;
  foreign: number;
  edges: number;
  sumLuma: number;
  sumLumaSq: number;
  lumaMin: number;
  lumaMax: number;
}

const EMPTY_METRICS: BackgroundMetrics = {
  uniformity: 0,
  edgeDensity: 0,
  clutterScore: 0,
  shadowScore: 0,
  colorCastScore: 0,
  meanColor: { r: 255, g: 255, b: 255 },
  meanLuma: 255,
  cornerLumaSpread: 0,
  targetDeviation: 0,
  maskCoverage: 0,
  subjectRect: { x: 0, y: 0, width: 1, height: 1 },
  issues: [],
  hasMask: false,
};

const colourDistance = (
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
): number => Math.hypot(a.r - b.r, a.g - b.g, a.b - b.b);

export const computeBackgroundMetrics = (input: BackgroundAnalysisInput): BackgroundMetrics => {
  const options: BackgroundOptions = { ...DEFAULT_BACKGROUND_OPTIONS, ...input.options };
  const { buffer } = input;
  const { width, height, channels } = buffer;
  const totalPixels = width * height;

  if (width < 16 || height < 16 || totalPixels < options.minBackgroundPixels) {
    return { ...EMPTY_METRICS, hasMask: false, issues: [] };
  }

  const subjectRect: NormalizedRect = {
    x: clamp(input.subjectRect.x - options.subjectPadding, 0, 1),
    y: clamp(input.subjectRect.y - options.subjectPadding, 0, 1),
    width: clamp(input.subjectRect.width + options.subjectPadding * 2, 0, 1),
    height: clamp(input.subjectRect.height + options.subjectPadding * 2, 0, 1),
  };

  /**
   * The subject is not only the head.
   *
   * In any portrait the neck, shoulders and torso continue below the chin and
   * normally span the whole frame width. If that band is treated as background,
   * the person's own clothing is reported as "clutter" and "shadow" — the single
   * most common false positive of this heuristic, which used to fail perfectly
   * good photos. Everything below the chin line therefore belongs to the
   * subject: what a document photo must keep plain is the background *around the
   * head*, which is exactly what this check still measures.
   */
  const headBottom = clamp(subjectRect.y + subjectRect.height * 0.85, 0, 1);
  const torso: NormalizedRect = {
    x: 0,
    y: headBottom,
    width: 1,
    height: 1 - headBottom,
  };

  const inside = (rect: NormalizedRect, nx: number, ny: number): boolean =>
    nx >= rect.x &&
    nx <= rect.x + rect.width &&
    ny >= rect.y &&
    ny <= rect.y + rect.height;

  // The face box does not include hair, ears or headwear, which the detector
  // never sees; widen the head region sideways so that the person's own head is
  // never mistaken for background clutter.
  const headSidePadding = subjectRect.width * options.hairPadding;
  const headTopPadding = subjectRect.height * options.hairTopPadding;
  const head: NormalizedRect = {
    x: clamp(subjectRect.x - headSidePadding, 0, 1),
    y: clamp(subjectRect.y - headTopPadding, 0, 1),
    width: clamp(subjectRect.width + headSidePadding * 2, 0, 1),
    height: subjectRect.height + headTopPadding,
  };

  const isSubject = (nx: number, ny: number): boolean =>
    inside(head, nx, ny) || inside(torso, nx, ny);

  // ---------------------------------------------------------------------
  // Pass 1 — sample the outer ring to establish the reference background.
  // ---------------------------------------------------------------------
  const ringR: number[] = [];
  const ringG: number[] = [];
  const ringB: number[] = [];
  const ringLuma: number[] = [];
  const step = Math.max(1, Math.floor(Math.min(width, height) / 180));

  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const nx = (x + 0.5) / width;
      const ny = (y + 0.5) / height;
      const onRing =
        nx < options.ringThickness ||
        nx > 1 - options.ringThickness ||
        ny < options.ringThickness ||
        ny > 1 - options.ringThickness;
      if (!onRing || isSubject(nx, ny)) continue;
      const rgb = rgbAt(buffer.data, width, channels, x, y);
      ringR.push(rgb.r);
      ringG.push(rgb.g);
      ringB.push(rgb.b);
      ringLuma.push(buffer.luma[y * width + x] as number);
    }
  }

  if (ringR.length < 32) {
    return {
      ...EMPTY_METRICS,
      hasMask: false,
      subjectRect,
      issues: [
        {
          kind: 'non_uniform',
          severity: 'warning',
          weight: 1,
          rect: { x: 0, y: 0, width: 1, height: 1 },
          message: 'Недостаточно фона вокруг головы для анализа — возможно, кадр слишком плотный.',
        },
      ],
    };
  }

  const reference = {
    r: median(ringR),
    g: median(ringG),
    b: median(ringB),
  };
  const referenceLuma = median(ringLuma);

  // ---------------------------------------------------------------------
  // Pass 2 — grid statistics over the whole frame.
  // ---------------------------------------------------------------------
  const gridSize = options.gridSize;
  const cells: CellStats[] = [];
  for (let gy = 0; gy < gridSize; gy += 1) {
    for (let gx = 0; gx < gridSize; gx += 1) {
      cells.push({
        x0: Math.floor((gx / gridSize) * width),
        y0: Math.floor((gy / gridSize) * height),
        x1: Math.max(Math.floor(((gx + 1) / gridSize) * width), Math.floor((gx / gridSize) * width) + 1),
        y1: Math.max(Math.floor(((gy + 1) / gridSize) * height), Math.floor((gy / gridSize) * height) + 1),
        count: 0,
        foreign: 0,
        edges: 0,
        sumLuma: 0,
        sumLumaSq: 0,
        lumaMin: 255,
        lumaMax: 0,
      });
    }
  }

  let backgroundPixels = 0;
  let edgePixels = 0;
  let foreignPixels = 0;
  let sumLuma = 0;
  let sumLumaSq = 0;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;

  for (let y = 0; y < height; y += 1) {
    const ny = (y + 0.5) / height;
    for (let x = 0; x < width; x += 1) {
      const nx = (x + 0.5) / width;
      if (isSubject(nx, ny)) continue;

      const value = buffer.luma[y * width + x] as number;
      const rgb = rgbAt(buffer.data, width, channels, x, y);
      const distance = colourDistance(rgb, reference);

      const right = x < width - 1 ? (buffer.luma[y * width + x + 1] as number) : value;
      const down = y < height - 1 ? (buffer.luma[(y + 1) * width + x] as number) : value;
      const gradient = Math.abs(right - value) + Math.abs(down - value);

      const cellIndex =
        Math.min(gridSize - 1, Math.floor(ny * gridSize)) * gridSize +
        Math.min(gridSize - 1, Math.floor(nx * gridSize));
      const cell = cells[cellIndex] as CellStats;

      backgroundPixels += 1;
      sumLuma += value;
      sumLumaSq += value * value;
      sumR += rgb.r;
      sumG += rgb.g;
      sumB += rgb.b;
      if (gradient > options.edgeThreshold) {
        edgePixels += 1;
        cell.edges += 1;
      }
      // A pixel that is background-positioned but not background-coloured is
      // an object, a pattern, or a second person.
      if (distance > options.colourTolerance) {
        foreignPixels += 1;
        cell.foreign += 1;
      }
      cell.count += 1;
      cell.sumLuma += value;
      cell.sumLumaSq += value * value;
      if (value < cell.lumaMin) cell.lumaMin = value;
      if (value > cell.lumaMax) cell.lumaMax = value;
    }
  }

  if (backgroundPixels < options.minBackgroundPixels) {
    return {
      ...EMPTY_METRICS,
      hasMask: false,
      subjectRect,
      issues: [
        {
          kind: 'non_uniform',
          severity: 'warning',
          weight: 1,
          rect: { x: 0, y: 0, width: 1, height: 1 },
          message: 'Голова занимает почти весь кадр — фон проанализировать не удалось.',
        },
      ],
    };
  }

  const cellMeans: number[] = [];
  const cellStdDevs: number[] = [];
  for (let index = 0; index < cells.length; index += 1) {
    const cell = cells[index] as CellStats;
    if (cell.count < 24) continue;
    const cellMean = cell.sumLuma / cell.count;
    cellMeans.push(cellMean);
    const variance = Math.max(0, cell.sumLumaSq / cell.count - cellMean * cellMean);
    cellStdDevs.push(Math.sqrt(variance));
  }

  const spread = cellMeans.length > 1 ? Math.max(...cellMeans) - Math.min(...cellMeans) : 0;
  const within = mean(cellStdDevs);
  const uniformity = 1 - clamp((spread * 0.6 + within * 0.4) / 40, 0, 1);

  const edgeDensity = edgePixels / backgroundPixels;
  const foreignRatio = foreignPixels / backgroundPixels;
  const clutterScore = clamp(foreignRatio * 3 + edgeDensity * 0.8, 0, 1);
  const shadowScore = clamp(spread / 45, 0, 1);

  const meanColor = { r: sumR / backgroundPixels, g: sumG / backgroundPixels, b: sumB / backgroundPixels };
  const meanLuma = sumLuma / backgroundPixels;
  const channelSpread = Math.max(meanColor.r, meanColor.g, meanColor.b) - Math.min(meanColor.r, meanColor.g, meanColor.b);
  const colorCastScore = clamp((channelSpread / 255) * 4, 0, 1);

  const corners = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ].map((corner) => {
    const gx = Math.min(gridSize - 1, Math.floor((corner.x === 0 ? 0.001 : 0.999) * gridSize));
    const gy = Math.min(gridSize - 1, Math.floor((corner.y === 0 ? 0.001 : 0.999) * gridSize));
    const cell = cells[gy * gridSize + gx] as CellStats;
    return cell.count > 0 ? cell.sumLuma / cell.count : meanLuma;
  });
  const cornerLumaSpread = Math.max(...corners) - Math.min(...corners);

  const target = input.targetColor ?? { r: 255, g: 255, b: 255 };
  const targetDeviation = clamp(colourDistance(meanColor, target) / 441, 0, 1);

  // ---------------------------------------------------------------------
  // Localized issues for the overlay.
  // ---------------------------------------------------------------------
  const issues: BackgroundIssue[] = [];
  const rectOfCell = (cell: CellStats): NormalizedRect => ({
    x: round(cell.x0 / width, 4),
    y: round(cell.y0 / height, 4),
    width: round(Math.max(0.02, (cell.x1 - cell.x0) / width), 4),
    height: round(Math.max(0.02, (cell.y1 - cell.y0) / height), 4),
  });

  const rankedByDarkness = cells
    .filter((cell) => cell.count >= 24)
    .map((cell) => ({ cell, mean: cell.sumLuma / cell.count }))
    .sort((a, b) => a.mean - b.mean);

  const darkest = rankedByDarkness[0];
  if (darkest && referenceLuma - darkest.mean > options.shadowDelta) {
    issues.push({
      kind: 'shadow',
      severity: referenceLuma - darkest.mean > options.shadowDelta * 2 ? 'error' : 'warning',
      weight: round(clamp((referenceLuma - darkest.mean) / 90, 0, 1), 3),
      rect: rectOfCell(darkest.cell),
      message: `На фоне есть затемнённый участок (яркость ниже остального фона на ${Math.round(referenceLuma - darkest.mean)}).`,
    });
  }

  const brightest = rankedByDarkness[rankedByDarkness.length - 1];
  if (
    brightest &&
    !issues.some((issue) => issue.kind === 'shadow') &&
    brightest.mean - referenceLuma > options.shadowDelta * 1.5
  ) {
    issues.push({
      kind: 'brightness',
      severity: 'warning',
      weight: round(clamp((brightest.mean - referenceLuma) / 90, 0, 1), 3),
      rect: rectOfCell(brightest.cell),
      message: 'Часть фона заметно светлее остального — возможна неравномерная подсветка.',
    });
  }

  const busiest = cells
    .filter((cell) => cell.count >= 24)
    .map((cell) => ({ cell, ratio: cell.foreign / cell.count }))
    .filter((item) => item.ratio > options.clutterCellRatio)
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 2);
  for (const item of busiest) {
    issues.push({
      kind: 'clutter',
      severity: item.ratio > 0.65 ? 'error' : 'warning',
      weight: round(clamp(item.ratio, 0, 1), 3),
      rect: rectOfCell(item.cell),
      message: 'В этой части фона есть посторонние объекты или детали интерьера.',
    });
  }

  if (colorCastScore > 0.25) {
    issues.push({
      kind: 'color_cast',
      severity: colorCastScore > 0.5 ? 'error' : 'warning',
      weight: round(colorCastScore, 3),
      rect: { x: 0, y: 0, width: 1, height: 1 },
      message: 'Фон имеет выраженный цветной оттенок вместо нейтрального.',
    });
  }

  if (targetDeviation > 0.18) {
    issues.push({
      kind: 'brightness',
      severity: targetDeviation > 0.35 ? 'error' : 'warning',
      weight: round(targetDeviation, 3),
      rect: { x: 0, y: 0, width: 1, height: 1 },
      message: 'Цвет фона отличается от требуемого светлого/белого.',
    });
  }

  if (uniformity < 0.55 && !issues.some((issue) => issue.kind === 'shadow')) {
    issues.push({
      kind: 'non_uniform',
      severity: uniformity < 0.35 ? 'error' : 'warning',
      weight: round(1 - uniformity, 3),
      rect: { x: 0, y: 0, width: 1, height: 1 },
      message: 'Фон неоднородный: видны переходы яркости или текстура.',
    });
  }

  return {
    uniformity: round(uniformity, 3),
    edgeDensity: round(edgeDensity, 3),
    clutterScore: round(clutterScore, 3),
    shadowScore: round(shadowScore, 3),
    colorCastScore: round(colorCastScore, 3),
    meanColor: { r: round(meanColor.r, 1), g: round(meanColor.g, 1), b: round(meanColor.b, 1) },
    meanLuma: round(meanLuma, 1),
    cornerLumaSpread: round(cornerLumaSpread, 1),
    targetDeviation: round(targetDeviation, 3),
    maskCoverage: round(backgroundPixels / totalPixels, 3),
    subjectRect,
    issues,
    hasMask: true,
  };
};
